import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, from, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AuthState } from '@apps/clos-sessions/state/auth';
import { environment } from '@apps/clos-sessions/environments';
import { BranchSdkParsedData } from './types';

@Injectable()
export class SessionInviteCreationEffect implements OnDestroy {
  protected destroy$ = new Subject<void>();

  constructor(
    protected store: Store,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  use(): void {
    combineLatest([
      this.activatedRoute.queryParamMap.pipe(
        map((params) => params.get('_branch_match_id')),
        filter(Boolean),
        distinctUntilChanged(),
      ),
      this.store.select(AuthState.isSignedIn).pipe(filter(Boolean)),
    ])
      .pipe(
        switchMap(([sessionHashedInvitationId]) => {
          return this.parseHashedInvitation(sessionHashedInvitationId);
        }),
        tap(({ sessionId: id }) => this.router.navigate(['sessions', id])),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  protected parseHashedInvitation(id: string): Observable<{ sessionId: string }> {
    return from(import('branch-sdk')).pipe(
      // techdebt https://github.com/Unoporoduction/CLOS_Frontend/issues/496
      // uglyhack
      // the "branch-sdk" package reassigns an export in its module at some point
      // leading to "TypeError: Cannot set property which has only a getter" error in runtime
      // [it's only assumption] Since the whole webpack module is immutable
      // it doesn't allow to perform the operation. In order to solve this,
      // the following module is additionally destructed
      map((module) => ({ ...module })),
      switchMap((BranchApi) => {
        return new Observable<BranchSdkParsedData>((subscriber) => {
          BranchApi.init(environment.branchIo.apiKey, { branch_match_id: id }, (err, result) => {
            if (err) {
              subscriber.error(err);
            } else {
              subscriber.next(result.data_parsed as BranchSdkParsedData);
            }

            subscriber.complete();
          });
        });
      }),
      map((parsed) => ({ sessionId: parsed._id })),
    );
  }
}
